import { Controller } from "@hotwired/stimulus"

/**
 * Ui::Calendar keyboard shortcuts.
 *
 * Listens on `document` (so the user doesn't have to focus the grid) and
 * dispatches calendar navigation by either:
 *   - replacing the calendar Turbo Frame's `src` (preferred — keeps URL in sync), or
 *   - falling back to `window.location.href`.
 *
 * Shortcuts:
 *   m  -> month view
 *   w  -> week view
 *   d  -> day view
 *   t  -> today (current view preserved)
 *   ←  -> previous period
 *   →  -> next period
 *   Esc -> close the topmost calendar modal (closest [data-calendar-modal])
 *
 * Modifiers (Cmd/Ctrl/Alt) suppress shortcuts so we don't fight browser
 * navigation. Typing in any input/textarea/select/contenteditable is also a
 * no-op (mirrors support--change-calendar).
 *
 * Markup example (rendered by T1):
 *
 *   <div data-controller="ui--calendar--keyboard-nav"
 *        data-ui--calendar--keyboard-nav-url-value="/support/admin/changes/calendar"
 *        data-ui--calendar--keyboard-nav-view-value="week"
 *        data-ui--calendar--keyboard-nav-frame-value="calendar-grid"
 *        data-ui--calendar--keyboard-nav-prev-url-value="/.../?week=-1"
 *        data-ui--calendar--keyboard-nav-next-url-value="/.../?week=+1"
 *        data-ui--calendar--keyboard-nav-today-url-value="/.../?week=0">
 *   </div>
 */
export default class extends Controller {
  static values = {
    url: String,
    view: { type: String, default: "week" },
    frame: { type: String, default: "calendar-grid" },
    prevUrl: String,
    nextUrl: String,
    todayUrl: String,
  }

  connect() {
    this._onKeyDown = this._onKeyDown.bind(this)
    document.addEventListener("keydown", this._onKeyDown)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeyDown)
  }

  // ── Public action methods (also callable from data-action attrs) ──────────

  switchToMonth() {
    this._switchTo("month")
  }

  switchToWeek() {
    this._switchTo("week")
  }

  switchToDay() {
    this._switchTo("day")
  }

  today() {
    if (this.hasTodayUrlValue) {
      this._navigate(this.todayUrlValue)
    } else if (this.hasUrlValue) {
      const u = new URL(this.urlValue, window.location.origin)
      u.searchParams.set("view", this.viewValue)
      this._navigate(u.toString())
    }
  }

  previousPeriod() {
    if (this.hasPrevUrlValue) this._navigate(this.prevUrlValue)
  }

  nextPeriod() {
    if (this.hasNextUrlValue) this._navigate(this.nextUrlValue)
  }

  closeModal() {
    const modal = document.querySelector("[data-calendar-modal]")
    if (modal) {
      if (typeof modal.close === "function") {
        modal.close()
      } else {
        modal.remove()
      }
    }
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  _onKeyDown(event) {
    if (event.metaKey || event.ctrlKey || event.altKey) return
    if (
      event.target &&
      typeof event.target.matches === "function" &&
      event.target.matches("input, textarea, select, [contenteditable]")
    ) {
      return
    }

    switch (event.key) {
      case "m":
        this.switchToMonth()
        event.preventDefault()
        break
      case "w":
        this.switchToWeek()
        event.preventDefault()
        break
      case "d":
        this.switchToDay()
        event.preventDefault()
        break
      case "t":
        this.today()
        event.preventDefault()
        break
      case "ArrowLeft":
        this.previousPeriod()
        event.preventDefault()
        break
      case "ArrowRight":
        this.nextPeriod()
        event.preventDefault()
        break
      case "Escape":
        this.closeModal()
        break
    }
  }

  _switchTo(view) {
    this.viewValue = view
    if (!this.hasUrlValue) return
    const u = new URL(this.urlValue, window.location.origin)
    u.searchParams.set("view", view)
    this._navigate(u.toString())
  }

  _navigate(href) {
    const frame = this.hasFrameValue
      ? document.getElementById(this.frameValue)
      : null
    if (frame && "src" in frame) {
      frame.src = href
    } else {
      window.location.href = href
    }
  }
}
